import { useEffect, useState } from "react";
import { X, Mail, Lock, User as UserIcon, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { http } from "@/lib/api";
import { useAuth, startGoogleLogin } from "@/lib/auth";

function formatErr(detail) {
  if (!detail) return "Something went wrong.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) return detail.map(e => e?.msg || JSON.stringify(e)).join(" ");
  return String(detail);
}

export default function AuthModal({ open, onClose, initialMode = "signin" }) {
  const [mode, setMode] = useState(initialMode); // 'signin' | 'signup'
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const { setUser } = useAuth();

  useEffect(() => { if (open) setMode(initialMode); }, [open, initialMode]);

  if (!open) return null;

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const path = mode === "signup" ? "/auth/register" : "/auth/login";
      const payload = mode === "signup" ? { email, password, name } : { email, password };
      const { data } = await http.post(path, payload);
      setUser({ ...data.user, is_subscribed: data.user.is_subscribed || false });
      toast.success(mode === "signup" ? "Welcome to SafeRxCheck!" : "Signed in");
      onClose?.();
    } catch (err) {
      toast.error(formatErr(err?.response?.data?.detail) || err?.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-end sm:items-center justify-center p-0 sm:p-4"
      style={{ background: "rgba(28,28,25,0.55)", backdropFilter: "blur(6px)" }}
      onClick={onClose}
      data-testid="auth-modal"
    >
      <div
        className="w-full max-w-md rounded-t-3xl sm:rounded-3xl p-6 sm:p-8 lg:p-10 relative"
        style={{ background: "white", border: "1px solid var(--border-default)" }}
        onClick={(e) => e.stopPropagation()}
      >
        <button
          onClick={onClose}
          className="absolute top-5 right-5 opacity-60 hover:opacity-100"
          aria-label="Close"
          data-testid="auth-modal-close"
        >
          <X className="h-5 w-5" />
        </button>

        <p className="label-small">{mode === "signup" ? "01 · Create account" : "01 · Welcome back"}</p>
        <h2
          className="font-serif-display text-3xl mt-2 tracking-tight"
          style={{ color: "var(--text-primary)" }}
          data-testid="auth-modal-title"
        >
          {mode === "signup" ? "Join SafeRxCheck" : "Sign in"}
        </h2>
        <p className="mt-2 text-sm" style={{ color: "var(--text-secondary)" }}>
          {mode === "signup"
            ? "Start with one free analysis. Upgrade anytime."
            : "Pick up where you left off."}
        </p>

        <form onSubmit={submit} className="mt-6 space-y-3" data-testid="auth-form">
          {mode === "signup" && (
            <label className="flex items-center gap-3 rounded-2xl border-2 px-4 py-3"
                   style={{ borderColor: "var(--border-default)", background: "var(--bg-main)" }}>
              <UserIcon className="h-4 w-4" style={{ color: "var(--text-muted)" }} />
              <input
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Your name"
                className="flex-1 bg-transparent outline-none"
                data-testid="auth-name-input"
              />
            </label>
          )}
          <label className="flex items-center gap-3 rounded-2xl border-2 px-4 py-3"
                 style={{ borderColor: "var(--border-default)", background: "var(--bg-main)" }}>
            <Mail className="h-4 w-4" style={{ color: "var(--text-muted)" }} />
            <input
              type="email"
              required
              autoComplete="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="you@example.com"
              className="flex-1 bg-transparent outline-none"
              data-testid="auth-email-input"
            />
          </label>
          <label className="flex items-center gap-3 rounded-2xl border-2 px-4 py-3"
                 style={{ borderColor: "var(--border-default)", background: "var(--bg-main)" }}>
            <Lock className="h-4 w-4" style={{ color: "var(--text-muted)" }} />
            <input
              type="password"
              required
              minLength={mode === "signup" ? 8 : 1}
              autoComplete={mode === "signup" ? "new-password" : "current-password"}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder={mode === "signup" ? "At least 8 characters" : "Your password"}
              className="flex-1 bg-transparent outline-none"
              data-testid="auth-password-input"
            />
          </label>

          <button
            type="submit"
            disabled={loading}
            className="w-full rounded-full py-3.5 text-white inline-flex items-center justify-center gap-2 transition-all duration-300 disabled:opacity-50"
            style={{ background: "var(--brand)" }}
            data-testid="auth-submit-btn"
          >
            {loading && <Loader2 className="h-4 w-4 animate-spin" />}
            {mode === "signup" ? "Create account" : "Sign in"}
          </button>
        </form>

        <div className="my-5 flex items-center gap-3">
          <span className="flex-1 h-px" style={{ background: "var(--border-default)" }} />
          <span className="label-small">OR</span>
          <span className="flex-1 h-px" style={{ background: "var(--border-default)" }} />
        </div>

        <button
          onClick={startGoogleLogin}
          className="w-full rounded-full py-3.5 border inline-flex items-center justify-center gap-2 transition-all"
          style={{ borderColor: "var(--border-default)", color: "var(--text-primary)" }}
          data-testid="auth-google-btn"
        >
          <svg aria-hidden="true" width="18" height="18" viewBox="0 0 48 48">
            <path fill="#FFC107" d="M43.6 20.5H42V20H24v8h11.3C33.9 32.5 29.4 36 24 36c-6.6 0-12-5.4-12-12s5.4-12 12-12c3 0 5.8 1.1 7.9 3l5.7-5.7C33.9 6.1 29.2 4 24 4 12.9 4 4 12.9 4 24s8.9 20 20 20 20-8.9 20-20c0-1.3-.1-2.3-.4-3.5z"/>
            <path fill="#FF3D00" d="M6.3 14.7l6.6 4.8C14.7 15.3 19 12 24 12c3 0 5.8 1.1 7.9 3l5.7-5.7C33.9 6.1 29.2 4 24 4 16.3 4 9.6 8.3 6.3 14.7z"/>
            <path fill="#4CAF50" d="M24 44c5.1 0 9.8-2 13.3-5.1l-6.1-5c-2 1.4-4.5 2.1-7.2 2.1-5.3 0-9.9-3.5-11.6-8.3l-6.5 5C9.4 39.6 16.1 44 24 44z"/>
            <path fill="#1976D2" d="M43.6 20.5H42V20H24v8h11.3c-.8 2.3-2.3 4.3-4.3 5.9l6.1 5C40 36 44 30.5 44 24c0-1.3-.1-2.3-.4-3.5z"/>
          </svg>
          Continue with Google
        </button>

        <p className="mt-6 text-sm text-center" style={{ color: "var(--text-secondary)" }}>
          {mode === "signup" ? (
            <>Already have an account?{" "}
              <button className="underline" onClick={() => setMode("signin")} data-testid="switch-to-signin">
                Sign in
              </button>
            </>
          ) : (
            <>New here?{" "}
              <button className="underline" onClick={() => setMode("signup")} data-testid="switch-to-signup">
                Create an account
              </button>
            </>
          )}
        </p>
      </div>
    </div>
  );
}
